import { useState, type KeyboardEvent } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { extractErrorMessage } from '../../../core/services';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { Pill } from '../../../shared/components/Pill';
import { useToast } from '../../../shared/components/Toast/ToastContext';
import type { EntityCardData } from './useEntityTypeEntities';

interface EntityKnowledgeCardProps {
  entity: EntityCardData;
  /** Whether the caller may flip the managed flag; the checkbox is read-only otherwise. */
  canEdit: boolean;
  /** This entity is the one open in the detail panel. */
  selected: boolean;
  /** Number of ENTITY-scope skill topics generated for this entity — shows the "N analyses" link when > 0. */
  analysisCount: number;
  /** Opens the entity in the detail panel — clicking anywhere on the card, its View button, or its analyses link. */
  onSelect: () => void;
  /** Persists the managed flag; rejects on failure, which the card reports as a toast. */
  onManagedChange: (isManaged: boolean) => Promise<void>;
}

function countLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** Pill sx override that flattens it to gray, for an unmanaged (disabled-looking) card. */
const disabledPillSx = { color: 'text.disabled', backgroundColor: 'action.disabledBackground', borderColor: 'divider' };

/**
 * One project entity of the managed type — name, description, a "Managed"
 * checkbox in the top-right corner (the user-curated `Entity.isManaged`
 * flag), three count pills (properties, relations, mentions), and an
 * "N analyses" link when an ENTITY-scope skill has been run for this entity.
 * The card is a button: selecting it (or its View/analyses link) opens
 * EntityDetailPanel with the full lists.
 *
 * An unmanaged entity's card renders disabled: grayed-out text and pills,
 * its View/analyses buttons and the card itself inert — the "Managed"
 * checkbox is the only live control, so the user can promote it back in.
 */
export function EntityKnowledgeCard({
  entity,
  canEdit,
  selected,
  analysisCount,
  onSelect,
  onManagedChange,
}: EntityKnowledgeCardProps) {
  const showToast = useToast();
  const [managedSubmitting, setManagedSubmitting] = useState(false);
  const disabled = !entity.isManaged;

  const handleManagedChange = async (isManaged: boolean) => {
    setManagedSubmitting(true);
    try {
      await onManagedChange(isManaged);
    } catch (e) {
      showToast({ title: 'Could not update managed flag', message: extractErrorMessage(e), severity: 'error' });
    } finally {
      setManagedSubmitting(false);
    }
  };

  // Enter/Space activate the card like a button, but only when the card
  // itself has focus — the same keys on the nested checkbox must keep
  // toggling it rather than opening the panel.
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <GlassPanel
      hover={!disabled}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-pressed={selected}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={disabled ? undefined : handleKeyDown}
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        minWidth: 0,
        cursor: disabled ? 'default' : 'pointer',
        // `.glass-panel.MuiPaper-root` paints the border at two-class
        // specificity, so the selected outline needs three (as SkillCard's
        // "has a run" tint does).
        ...(selected ? { '&&.MuiPaper-root': { borderColor: 'var(--color-primary)' } } : {}),
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.3, color: disabled ? 'text.disabled' : undefined }}>
            {entity.name}
          </Typography>
          {entity.description && (
            <Typography variant="body2" color={disabled ? 'text.disabled' : 'text.secondary'} sx={{ mt: 0.5 }}>
              {entity.description}
            </Typography>
          )}
        </Box>
        <FormControlLabel
          labelPlacement="start"
          label={
            <Typography variant="caption" color="text.secondary">
              Managed
            </Typography>
          }
          control={
            <Checkbox
              size="small"
              checked={entity.isManaged}
              disabled={!canEdit || managedSubmitting}
              onChange={(e) => handleManagedChange(e.target.checked)}
              sx={{ p: '4px' }}
            />
          }
          // Toggling the flag must not also open the panel.
          onClick={(e) => e.stopPropagation()}
          sx={{ m: 0, mr: -0.5, flexShrink: 0 }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.75} useFlexGap flexWrap="wrap">
        <Pill color="primary" sx={disabled ? disabledPillSx : undefined}>
          {countLabel(entity.properties.length, 'property', 'properties')}
        </Pill>
        <Pill color="secondary" sx={disabled ? disabledPillSx : undefined}>
          {countLabel(entity.relations.length, 'relation', 'relations')}
        </Pill>
        <Pill color="cyan" sx={disabled ? disabledPillSx : undefined}>
          {countLabel(entity.mentions.length, 'mention', 'mentions')}
        </Pill>
        {analysisCount > 0 && (
          <Button
            size="small"
            variant="text"
            color="success"
            disabled={disabled}
            startIcon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            sx={{ ml: 'auto' }}
          >
            {countLabel(analysisCount, 'analysis', 'analyses')}
          </Button>
        )}
        <Button
          size="small"
          variant="text"
          disabled={disabled}
          endIcon={<ArrowForwardIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          sx={analysisCount > 0 ? {} : { ml: 'auto' }}
        >
          View
        </Button>
      </Stack>
    </GlassPanel>
  );
}
